import KML from 'ol/format/KML';
import GPX from 'ol/format/GPX';
import { Layer } from '../../models/Layers';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { config } from '../../config/Config';
import { Fill, Style, Stroke, Circle } from 'ol/style';
import { getTranslatedKey } from '../Utils/TranslateUtils';
import { toast } from 'react-toastify';
import GML2 from 'ol/format/GML2.js';
import GML3 from 'ol/format/GML3.js';
import GML32 from 'ol/format/GML32.js';
import { Feature } from 'ol';

const featureStyle = new Style({
  image: new Circle({
    radius: 5,
    fill: new Fill({
      color: '#febf0060'
    }),
    stroke: new Stroke({
      color: '#febf00',
      width: 2
    })
  }),
  fill: new Fill({
    color: '#febf0060'
  }),
  stroke: new Stroke({
    color: '#febf00',
    width: 2,
    lineDash: null
  })
});

/**
 *
 * @param {*} event
 * @param {*} map
 * @param {*} arrayLayers
 * @desc funcion para cargar capa desde fichero
 */
export function onChangeFilesUploaded(file, map, arrayLayers, handleLayerChange) {
  let extension = file.name.split('.').pop();
  let nameFile = file.name;
  let features;
  let reader = new FileReader();
  reader.readAsText(file);
  if (file && (extension === 'kml' || extension === 'gpx' || extension === 'gml' || extension === 'json' || extension === 'geojson')) {
    reader.onload = function (evt) {
      features = convertEPSG(extension, evt.target.result);
      if (features.length === 0) {
        return toast.error(getTranslatedKey('loadFileError'));
      }
      loadFeatureCollection(features, map, arrayLayers, handleLayerChange, nameFile);
    };
  } else {
    toast.error(getTranslatedKey('error_incompatibleFormat'), { autoClose: 4000, pauseOnHover: true });
  }
}

/**
 *
 * @param {*} extension
 * @param {*} fileResult
 * @funcion para hacer la conversion a la proyeccion del mapa
 */
const convertEPSG = (extension, fileResult) => {
  const formatMap = {
    json: [GeoJSON],
    geojson: [GeoJSON],
    gpx: [GPX],
    kml: [KML],
    gml: [GML32, GML3, GML2]
  };

  for (const Format of formatMap[extension]) {
    const format = new Format();
    const features = getFeatures(format, fileResult);

    if (features && features.length > 0 && checkGeoms(features[0])) {
      return features;
    }
  }
  toast.error(getTranslatedKey('error_processFile'));
};
const checkGeoms = (feat) => {
  if (feat.getGeometry().getType() === 'GeometryCollection') {
    if (feat.getGeometry().getGeometries()[0].getCoordinates().length > 0) return true;
  } else {
    if (feat.getGeometry().getCoordinates().length > 0) return true;
  }
  return false;
};
const getFeatures = (format, fileResult) => {
  try {
    const projection = format.readProjection(fileResult)?.getCode();
    const features = format.readFeatures(fileResult, {
      dataProjection: projection || config.EPSG,
      featureProjection: config.EPSG_mapa
    });
    return features;
  } catch (error) {
    return null;
  }
};

export const loadFeatureCollection = (features, map, arrayLayers, handleLayerChange, name) => {
  let points = [];
  let polygons = [];
  let lines = [];
  features.forEach((feature) => {
    let geomType = feature.getGeometry().getType();
    if (geomType === 'GeometryCollection') {
      let geometries = feature.getGeometry().getGeometries();
      let features = geometries.map((geometry) => {
        let newFeat = new Feature({
          geometry: geometry
        });
        newFeat.setStyle(feature.getStyle());
        return newFeat;
      });
      features.forEach((feat) => {
        let type = featureTypeCheck(feat.getGeometry().getType());
        if (type === 'Point') {
          points.push(feat);
        } else if (type === 'LineString') {
          lines.push(feat);
        } else if (type === 'Polygon') {
          polygons.push(feat);
        }
      });
    }

    let type = featureTypeCheck(geomType);
    if (type === 'Point') {
      points.push(feature);
    } else if (type === 'LineString') {
      lines.push(feature);
    } else if (type === 'Polygon') {
      polygons.push(feature);
    }
  });
  if (points.length > 0) {
    let layerName = name + ' Punts' || 'Punts';
    loadExternalFile(points, layerName, map, arrayLayers, handleLayerChange, 'Punt');
  }
  if (polygons.length > 0) {
    let layerName = name + ' Polígons' || 'Polígons';
    loadExternalFile(polygons, layerName, map, arrayLayers, handleLayerChange, 'Polígon');
  }
  if (lines.length > 0) {
    let layerName = name + ' Línies' || 'Línies';
    loadExternalFile(lines, layerName, map, arrayLayers, handleLayerChange, 'Línia');
  }

  // const nonEmptyArrays = [points, polygons, lines].filter((arr) => arr.length > 0);
  // if (nonEmptyArrays.length > 1) {
  //   loadExternalFile(fc, name, map, arrayLayers, handleLayerChange, 'MultiGeom');
  // } else {
  //   let rep;
  //   if (points.length > 0) {
  //     rep = 'Punt';
  //   } else if (polygons.length > 0) {
  //     rep = 'Polígon';
  //   } else {
  //     rep = 'Línia';
  //   }
  //   loadExternalFile(fc, name, map, arrayLayers, handleLayerChange, rep);
  // }
};

export const featureTypeCheck = (type) => {
  if (type === 'OpenLayers.Geometry.MultiPoint' || type === 'Point' || type === 'OpenLayers.Geometry.Point' || type === 'MultiPoint') {
    return 'Point';
  } else if (
    type === 'LineString' ||
    type === 'OpenLayers.Geometry.LineString' ||
    type === 'MultiLineString' ||
    type === 'OpenLayers.Geometry.MultiLineString'
  ) {
    return 'LineString';
  } else if (type === 'Polygon' || type === 'OpenLayers.Geometry.MultiPolygon' || type === 'OpenLayers.Geometry.Polygon' || type === 'MultiPolygon') {
    return 'Polygon';
  } else if (type === 'GeometryCollection') {
    return 'GeometryCollection';
  }
};

/**
 *
 * @param {*} fileResult
 * @param {*} nameAbstract
 * @param {*} map
 * @param {*} arrayLayers
 * @desc funcion que carga el al mapa una lista de features en proyección 3857
 */
function loadExternalFile(fileResult, nameAbstract, map, arrayLayers, handleLayerChange, type) {
  let extension = nameAbstract.split('.').pop().substr(0, 3);

  if (arrayLayers.find((l) => l.getTitle() === nameAbstract) === undefined) {
    let vectorLayer = new VectorLayer({
      source: new VectorSource({ features: fileResult }),
      style: featureStyle
    });
    let sitepLayer = new Layer(vectorLayer, false, true);
    sitepLayer.setTitle(nameAbstract);
    sitepLayer.setName(nameAbstract);
    sitepLayer.setFileName(type);
    sitepLayer.setTemporal();
    sitepLayer.setOLLayer(vectorLayer);
    sitepLayer.setTempBbox();
    if (extension === 'kml') sitepLayer.setKML();
    arrayLayers.unshift(sitepLayer);
    handleLayerChange();
    map.addLayer(vectorLayer);
  }
}
